// Reads the signed-in user's Zitadel session so the service modules can authenticate their calls to
// the Slack API.
//
// The services are plain modules rather than components, so they cannot reach the session through
// useAuth(). They read the same localStorage entry that oidc-client-ts (via @zitadel/react) writes
// and that AuthContext already reads, which keeps one source of truth and no prop-drilling.

const storageKey = () =>
  `oidc.user:${process.env.REACT_APP_ZITADEL_AUTHORITY}:${process.env.REACT_APP_ZITADEL_CLIENT_ID}`;

/** The persisted oidc-client-ts user, or null when signed out or the entry is unreadable. */
export const getStoredUser = () => {
  try {
    const raw = window.localStorage.getItem(storageKey());
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    // A malformed entry should log the user out cleanly, not crash every API call.
    console.error('Could not read the stored Zitadel session:', error);
    return null;
  }
};

/**
 * The token the Slack API expects.
 *
 * Deliberately access_token and not id_token. The API verifies callers by handing the token to
 * Zitadel's introspection endpoint; an id_token is issued to this browser app rather than to the
 * API, so introspection reports it inactive and every request comes back 401.
 */
export const getAccessToken = () => getStoredUser()?.access_token || '';

/** True once the stored token has expired, so callers can prompt a fresh sign-in. */
export const isAccessTokenExpired = () => {
  const expiresAt = getStoredUser()?.expires_at;
  // expires_at is in seconds since the epoch. Absent means we cannot tell, so assume still valid
  // and let the API be the judge.
  return typeof expiresAt === 'number' && expiresAt * 1000 <= Date.now();
};

/**
 * Standard headers for a call to the Slack API. Pass false for requests with no body, so no
 * Content-Type is advertised.
 */
export const getAuthHeaders = (includeContentType = true) => {
  const headers = { Accept: '*/*' };

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};
